import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBaseUrl } from '../../apiBase';

const API = getApiBaseUrl();

function adminApi(token) {
    return axios.create({
        baseURL: API,
        headers: { Authorization: `Bearer ${token}` }
    });
}

function prevMonthDefaults() {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
}

async function downloadFile(token, path, filename) {
    const res = await fetch(`${API}${path}`, {
        headers: { Authorization: `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
}

export default function AdminMonthlyReportTab({ token }) {
    const defaults = prevMonthDefaults();
    const [year, setYear] = useState(defaults.year);
    const [month, setMonth] = useState(defaults.month);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');
    const [err, setErr] = useState('');

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            setErr('');
            try {
                const res = await adminApi(token).get('/admin/reports/monthly-summary', {
                    params: { year, month }
                });
                if (!cancelled) setData(res.data);
            } catch (e) {
                if (!cancelled) setErr(e.response?.data?.message || 'Ошибка загрузки');
            }
            if (!cancelled) setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [token, year, month]);

    async function handleDownload() {
        try {
            await downloadFile(
                token,
                `/admin/reports/export/monthly-summary.xlsx?year=${year}&month=${month}`,
                `monthly-summary-${year}-${String(month).padStart(2, '0')}.xlsx`
            );
        } catch {
            setErr('Не удалось скачать Excel');
        }
    }

    async function handleEmail() {
        setMsg('');
        try {
            const res = await adminApi(token).post('/admin/reports/send-email', {
                reportType: 'monthly-summary',
                period: { year, month }
            });
            setMsg(res.data?.message || 'Отправлено');
        } catch (e) {
            setErr(e.response?.data?.message || 'Ошибка отправки');
        }
    }

    const fmt = (n) => (n ?? 0).toLocaleString('ru-RU');

    return (
        <div>
            <div className="sp-panel sp-toolbar" style={{ marginBottom: 16, padding: 14, display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center' }}>
                <label style={{ fontSize: 12 }}>
                    Год{' '}
                    <input type="number" value={year} onChange={e => setYear(Number(e.target.value))} style={{ width: 80, padding: 6, borderRadius: 8, marginLeft: 4 }} />
                </label>
                <label style={{ fontSize: 12 }}>
                    Месяц{' '}
                    <input type="number" min={1} max={12} value={month} onChange={e => setMonth(Number(e.target.value))} style={{ width: 60, padding: 6, borderRadius: 8, marginLeft: 4 }} />
                </label>
                <button type="button" className="sp-btn sp-btn--secondary" onClick={handleDownload}>📥 Скачать Excel</button>
                <button type="button" className="sp-btn sp-btn--primary" onClick={handleEmail}>📧 Отправить на email</button>
            </div>

            {loading && <div style={{ padding: 20, color: '#64748b' }}>Загрузка…</div>}
            {err && <div style={{ padding: 12, marginBottom: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 8 }}>{err}</div>}
            {msg && <div style={{ padding: 12, marginBottom: 12, background: '#ecfdf5', color: '#065f46', borderRadius: 8 }}>{msg}</div>}

            {data && !loading && (
                <>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
                        {[
                            ['Новые пользователи', data.newUsers],
                            ['Всего пользователей', data.totalUsersEndOfMonth],
                            ['Новые школы', data.newSchools],
                            ['Платежи', `${fmt(data.paymentsReceived)} ₽`],
                            ['Доп. начисления', `${fmt(data.extraChargesInvoiced)} ₽`],
                            ['Среднее активных/школа', data.avgActiveStudentsPerSchool]
                        ].map(([label, value]) => (
                            <div key={label} className="sp-panel" style={{ padding: 14 }}>
                                <div style={{ fontSize: 11, color: '#64748b' }}>{label}</div>
                                <div style={{ fontSize: 20, fontWeight: 800 }}>{value}</div>
                            </div>
                        ))}
                    </div>

                    <section className="sp-panel" style={{ padding: 16, marginBottom: 16 }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800 }}>Начисления за доп. учеников</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={{ padding: 8, textAlign: 'left' }}>Школа</th>
                                    <th style={{ padding: 8 }}>Peak</th>
                                    <th style={{ padding: 8 }}>Extra</th>
                                    <th style={{ padding: 8 }}>Сумма</th>
                                    <th style={{ padding: 8 }}>Статус</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.charges || []).map((c, i) => (
                                    <tr key={`${c.schoolId}-${i}`} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: 8 }}>{c.schoolName}</td>
                                        <td style={{ padding: 8, textAlign: 'center' }}>{c.peakStudents}</td>
                                        <td style={{ padding: 8, textAlign: 'center' }}>{c.extraStudents}</td>
                                        <td style={{ padding: 8, textAlign: 'right' }}>{fmt(c.extraCharge)} ₽</td>
                                        <td style={{ padding: 8 }}>{c.status}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>

                    <section className="sp-panel" style={{ padding: 16 }}>
                        <h3 style={{ margin: '0 0 10px', fontSize: 15, fontWeight: 800 }}>Платежи</h3>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                    <th style={{ padding: 8, textAlign: 'left' }}>Дата</th>
                                    <th style={{ padding: 8, textAlign: 'left' }}>Школа</th>
                                    <th style={{ padding: 8 }}>Сумма</th>
                                    <th style={{ padding: 8 }}>Тип</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(data.payments || []).map(p => (
                                    <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: 8 }}>{p.confirmedAt ? new Date(p.confirmedAt).toLocaleDateString('ru-RU') : '—'}</td>
                                        <td style={{ padding: 8 }}>{p.schoolName || '—'}</td>
                                        <td style={{ padding: 8, textAlign: 'right' }}>{fmt(p.amount)} ₽</td>
                                        <td style={{ padding: 8 }}>{p.paymentType}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </section>
                </>
            )}
        </div>
    );
}
